import PropTypes from 'prop-types'
import { useRouter } from 'next/router'
import React, { useEffect, useId, useRef, useState } from 'react'
import Typography from 'src/components/shared/Typography'

const INVALID_PASSWORD_MESSAGE = 'The password you entered is incorrect.'
const LOCKED_MESSAGE = 'Too many attempts. Please try again later.'
const UNLOCK_ERROR_MESSAGE =
  'We could not unlock this content right now. Please try again.'
const DESCRIPTION =
  "Freedom Debt Relief's personal finance education content is home to original, data-driven research and exclusive story angles available for editorial pickup. Content is organized by financial topic and ready for journalists and outreach partners to consider. Enter the password below to view this content."

const PasswordProtectedGate = ({ entryId }) => {
  const router = useRouter()
  const [password, setPassword] = useState('')
  const [error, setError] = useState(null)
  const [isLocked, setIsLocked] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const lockTimer = useRef(null)
  const inputId = useId()
  const errorId = `${inputId}-error`

  useEffect(() => {
    setPassword('')
    setError(null)
    setIsLocked(false)

    if (lockTimer.current) {
      clearTimeout(lockTimer.current)
      lockTimer.current = null
    }

    return () => {
      if (lockTimer.current) {
        clearTimeout(lockTimer.current)
      }
    }
  }, [entryId])

  const onChange = (event) => {
    setPassword(event.target.value)
    setError(null)
  }

  const onSubmit = async (event) => {
    event.preventDefault()

    if (!password || isSubmitting || isLocked) {
      return
    }

    setError(null)
    setIsSubmitting(true)

    try {
      const response = await fetch('/api/content-password/unlock/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ entryId, password }),
      })

      if (!response.ok) {
        const locked = response.status === 429
        let nextError = UNLOCK_ERROR_MESSAGE

        if (locked) {
          nextError = LOCKED_MESSAGE
          const retryAfterSeconds = Number(
            response.headers?.get?.('Retry-After')
          )

          if (Number.isFinite(retryAfterSeconds) && retryAfterSeconds > 0) {
            if (lockTimer.current) {
              clearTimeout(lockTimer.current)
            }

            setIsLocked(true)
            lockTimer.current = setTimeout(() => {
              lockTimer.current = null
              setIsLocked(false)
              setError((currentError) =>
                currentError === LOCKED_MESSAGE ? null : currentError
              )
            }, retryAfterSeconds * 1000)
          } else {
            setIsLocked(false)
          }
        } else if (response.status === 401) {
          nextError = INVALID_PASSWORD_MESSAGE
          setIsLocked(false)
        }

        setError(nextError)
        setPassword('')

        return
      }

      await router.replace(router.asPath, undefined, { scroll: false })
    } catch {
      setError(UNLOCK_ERROR_MESSAGE)
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <section className="padding-x-primary w-full">
      <div className="max-w-160 mx-auto flex w-full flex-col items-center gap-4 py-8 text-center md:py-12">
        <Typography
          variant="displayLarge"
          component="h1"
          lg={{ variant: 'displayXLarge' }}
          align="center"
        >
          This content is password protected
        </Typography>
        <img
          src="/next-assets/icons/password-protected.svg"
          alt=""
          aria-hidden="true"
          className="size-[104px]"
        />
        <Typography
          variant="bodyMedium"
          component="p"
          color="secondary"
          lg={{ variant: 'bodyLarge' }}
          align="center"
        >
          {DESCRIPTION}
        </Typography>
        <form
          className="flex w-full flex-col items-center gap-2"
          onSubmit={onSubmit}
          noValidate
        >
          <div className="flex w-full max-w-80 flex-col gap-1">
            <label htmlFor={inputId}>
              <Typography variant="labelMedium" component="span">
                Password
              </Typography>
            </label>
            <input
              id={inputId}
              type="password"
              name="password"
              value={password}
              onChange={onChange}
              placeholder="Enter your password"
              autoComplete="off"
              disabled={isSubmitting || isLocked}
              aria-invalid={Boolean(error)}
              aria-describedby={error ? errorId : undefined}
              className={`h-14 rounded-lg border bg-white px-4 text-body-medium text-content-primary outline-none placeholder:text-content-secondary ${
                error ? 'border-red-600' : 'border-primary'
              } focus:border-blue-accent`}
            />
          </div>
          <div className="min-h-6 w-full max-w-80" aria-live="polite">
            {error && (
              <p id={errorId} className="text-left text-sm text-red-600">
                {error}
              </p>
            )}
          </div>
          <button
            type="submit"
            disabled={isSubmitting || isLocked || !password}
            aria-busy={isSubmitting}
            className="min-h-12 rounded-lg bg-blue-accent px-6 py-2 text-label-button-medium text-white transition-colors hover:bg-blue-600 disabled:cursor-not-allowed disabled:bg-gray-100 disabled:text-gray-230"
          >
            {isSubmitting ? 'Entering...' : 'Enter'}
          </button>
        </form>
      </div>
    </section>
  )
}

PasswordProtectedGate.propTypes = {
  entryId: PropTypes.string.isRequired,
}

export default PasswordProtectedGate
